"use client";

// Comments on a grading RUN — one thread per run.
//
// The third argument-shaped store, and it keys on the third kind of subject.
// Script comments coordinate the people working a script; rubric comments argue
// with the standard; these argue with one application of that standard to one
// script at one moment. That is the unit an SME actually disagrees with — not
// "the rubric is wrong" but "on this script, this ask was wrong" — and it is
// what makes a thread here usable as training data later.
//
// Keying on `runId` rather than `scriptId` is the whole point, and it is the
// same reasoning as the rubric store's version key: a run is immutable, so a
// note attached to one keeps pointing at exactly the text and the fixes it was
// written about, however much the script is rewritten afterwards. The anchor
// cannot go stale, because nothing underneath it can change.
//
// `scriptId` rides along denormalized so a triage list can group by script
// without loading every run.

import { uuidv7 } from "uuidv7";
import { persist, isConfirmed } from "./store-persist";
import { sameData } from "./same-data";
import type { CommentAnchor } from "./ironside-script-store";

/** The triage ladder, shared with the legacy grader-feedback store: an admin
 *  marks a note reviewed when they have read it, actioned when the rubric or the
 *  model has actually moved because of it. */
export type RunCommentStatus = "new" | "reviewed" | "actioned";

export interface RunComment {
  id: string; // uuidv7 — time-ordered, so it self-sorts chronologically
  /** The grading run this note argues with. */
  runId: string;
  /** Denormalized, for grouping a triage list by script. */
  scriptId: string;
  /** The signed-in email. Comparing display names breaks the moment two people share one. */
  authorId: string;
  authorName: string;
  /** The role the author actually HOLDS, not the lens they were wearing. Tooltip only. */
  authorRole: string;
  text: string;
  postedAtMs: number;
  /** Presence renders "(edited)". */
  editedAtMs?: number;
  /** Tombstone, kept in the row so the text survives for audit. /api/store's DELETE refuses comment kinds. */
  deleted: boolean;
  /** Optional region anchor — a specific suggested fix. Absent = a note about
   *  the grading as a whole. */
  anchor?: CommentAnchor;
  /** Triage state. Optional on the row and defaulted on read, so comments
   *  written before the feedback console showed them are "new" rather than
   *  undefined — nothing has to be backfilled for the ladder to work. */
  status?: RunCommentStatus;
}

/**
 * The editor's grade panel asks two questions — the one-line disagreement and
 * the reasoning — and a run comment holds one body. This is the convention that
 * joins them, kept here rather than in the widget because it defines what those
 * rows LOOK like forever: the console renders them, and the export ships them to
 * whatever reads the corpus next.
 *
 * The labels stay in the text on purpose. Splitting the fields back out at read
 * time would need a parser, and a parser over user-entered prose is a thing that
 * silently mis-splits the first time somebody types "Why it's wrong:" inside
 * their own reasoning.
 */
export function formatCorrection(whatsWrong: string, whyAndInstead: string): string {
  const head = `What's wrong: ${whatsWrong.trim()}`;
  const why = whyAndInstead.trim();
  // The reasoning is optional at submit time — the one-liner is the only
  // required field — so an empty one leaves no dangling label.
  return why ? `${head}\nWhy it's wrong: ${why}` : head;
}

export interface NewRunComment {
  runId: string;
  scriptId: string;
  authorId: string;
  authorName: string;
  authorRole: string;
  text: string;
  anchor?: CommentAnchor;
}

// Keyed by comment id — one store row per comment, so two people commenting on
// the same run at the same time merge instead of clobbering.
const comments = new Map<string, RunComment>();
const listeners = new Set<() => void>();
let version = 0;

function notify(): void {
  version++;
  listeners.forEach((l) => l());
}

function write(c: RunComment): void {
  comments.set(c.id, c);
  persist("run_comment", c.id, c);
  notify();
}

/** Throws on an empty authorId — see addComment in ironside-script-store for
 *  why: an empty one makes canModifyComment match everyone against everyone. */
export function addRunComment(input: NewRunComment): void {
  if (!input.authorId) throw new Error("addRunComment: authorId is required");
  if (!input.text.trim()) return;
  write({
    id: uuidv7(),
    runId: input.runId,
    scriptId: input.scriptId,
    authorId: input.authorId,
    authorName: input.authorName,
    authorRole: input.authorRole,
    text: input.text.trim(),
    postedAtMs: Date.now(),
    deleted: false,
    anchor: input.anchor,
  });
}

/** Move a note along the triage ladder. Separate from editRunComment because
 *  triage is the READER's action on somebody else's note, not an edit of it —
 *  it must not stamp editedAtMs or require authorship. */
export function setRunCommentStatus(id: string, status: RunCommentStatus): void {
  const existing = comments.get(id);
  if (!existing || existing.deleted) return;
  write({ ...existing, status });
}

export function editRunComment(id: string, text: string): void {
  const existing = comments.get(id);
  if (!existing || existing.deleted || !text.trim()) return;
  write({ ...existing, text: text.trim(), editedAtMs: Date.now() });
}

/** A tombstone, not a delete — /api/store's DELETE refuses comment kinds. */
export function deleteRunComment(id: string): void {
  const existing = comments.get(id);
  if (!existing || existing.deleted) return;
  write({ ...existing, deleted: true });
}

/** One run's thread, oldest first (uuidv7 sorts chronologically). */
export function getRunComments(runId: string): RunComment[] {
  return Array.from(comments.values())
    .filter((c) => c.runId === runId && !c.deleted)
    .sort((a, b) => a.id.localeCompare(b.id));
}

/** Every live comment, newest first — for a triage list, where a note is one
 *  item to action rather than one turn in a conversation. */
export function getAllRunComments(): RunComment[] {
  return Array.from(comments.values())
    .filter((c) => !c.deleted)
    .sort((a, b) => b.id.localeCompare(a.id));
}

/** Per-run counts, so a history list can show which gradings have been argued
 *  with without opening each one. */
export function getRunCommentCounts(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of comments.values()) {
    if (c.deleted) continue;
    out[c.runId] = (out[c.runId] ?? 0) + 1;
  }
  return out;
}

export function subscribeRunComments(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getRunCommentsVersion(): number {
  return version;
}

// ─── Hydration from the server ───────────────────────────────────────────────
export function applyServerRows(
  rows: { kind: string; id: string; data: unknown }[],
  deleted: { kind: string; id: string }[] = [],
): void {
  let changed = false;
  for (const d of deleted) {
    // A key deleted on the server leaves the map.
    if (d.kind !== "run_comment" || !isConfirmed(d.kind, d.id, null)) continue;
    if (comments.delete(d.id)) changed = true;
  }
  for (const r of rows) {
    if (r.kind !== "run_comment") continue;
    if (!isConfirmed(r.kind, r.id, r.data)) continue;
    if (comments.has(r.id) && sameData(comments.get(r.id), r.data)) continue; // nothing new
    comments.set(r.id, r.data as RunComment);
    changed = true;
  }
  if (changed) notify();
}
